import { z } from 'zod';

import { SIDES, TRADE_TYPES } from '../enums';
import { AccountIdSchema } from './account';

/**
 * Trade Plan input schema.
 * Used by both POST /trade-plans (server) and the TradePlanForm (client).
 *
 * Stop-direction invariant: stop must be on the loss side of entry.
 * If this refinement fails, the gate cannot evaluate correctly.
 */
export const TradePlanInputSchema = z
  .object({
    accountId: AccountIdSchema,
    symbol: z
      .string()
      .min(1, 'Symbol is required.')
      .max(12)
      .regex(/^[A-Z0-9.-]+$/, 'Symbol must be uppercase letters, digits, dots, or hyphens.'),
    side: z.enum(SIDES),
    proposedEntry: z
      .number({ invalid_type_error: 'Entry is required.' })
      .positive('Entry must be greater than zero.'),
    plannedEntryMin: z.number().positive('Minimum entry must be greater than zero.').optional(),
    plannedEntryMax: z.number().positive('Maximum entry must be greater than zero.').optional(),
    proposedStop: z.number().positive('Stop must be greater than zero.').optional(),
    proposedTarget: z.number().positive('Target must be greater than zero.').optional(),
    proposedQty: z.number().positive('Quantity must be greater than zero.').optional(),
    theme: z.string().max(40).optional(),
    tradeType: z.enum(TRADE_TYPES),
    conviction: z.number().int().min(1).max(3),
    thesis: z.string().trim().min(1, 'Thesis is required.').max(500),
    whyNow: z.string().max(500).optional(),
    invalidation: z.string().trim().min(1, 'Invalidation is required.').max(500),
    isFtdPilot: z.boolean().default(false),
    overrideReason: z
      .string()
      .trim()
      .min(5, 'Override reason must be at least 5 characters.')
      .max(300)
      .optional(),
    checklist: z.record(z.string(), z.boolean()).optional(),
    validUntil: z.string().datetime().optional(),
  })
  .refine(
    (d) => {
      if (d.proposedStop === undefined) return true;
      return d.side === 'BUY' ? d.proposedStop < d.proposedEntry : d.proposedStop > d.proposedEntry;
    },
    {
      message: 'Stop must be on the loss side of entry.',
      path: ['proposedStop'],
    },
  )
  .refine(
    (d) => {
      if (!d.proposedTarget || d.proposedStop === undefined) return true;
      return d.side === 'BUY'
        ? d.proposedTarget > d.proposedEntry
        : d.proposedTarget < d.proposedEntry;
    },
    {
      message: 'Target must be on the profit side of entry.',
      path: ['proposedTarget'],
    },
  )
  .superRefine((data, context) => {
    if ((data.plannedEntryMin === undefined) !== (data.plannedEntryMax === undefined)) {
      const missingPath =
        data.plannedEntryMin === undefined ? 'plannedEntryMin' : 'plannedEntryMax';
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter both planned entry range bounds or leave both blank.',
        path: [missingPath],
      });
      return;
    }
    if (
      data.plannedEntryMin !== undefined &&
      data.plannedEntryMax !== undefined &&
      data.plannedEntryMin > data.plannedEntryMax
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Maximum entry must be greater than or equal to minimum entry.',
        path: ['plannedEntryMax'],
      });
    }
  });

export type TradePlanInput = z.infer<typeof TradePlanInputSchema>;

export const TradePlanPreviewInputSchema = z.object({
  accountId: AccountIdSchema,
  symbol: z
    .string()
    .trim()
    .min(1)
    .max(12)
    .regex(/^[A-Z0-9.-]+$/),
  side: z.enum(SIDES),
  proposedEntry: z.number().positive(),
  proposedStop: z.number().positive().optional(),
  proposedTarget: z.number().positive().optional(),
  proposedQty: z.number().positive().optional(),
  theme: z.string().max(40).optional(),
  tradeType: z.enum(TRADE_TYPES),
  conviction: z.number().int().min(1).max(3),
  thesis: z.string().max(500),
  invalidation: z.string().max(500).optional(),
  isFtdPilot: z.boolean().default(false),
});

export type TradePlanPreviewInput = z.infer<typeof TradePlanPreviewInputSchema>;
